import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Bell, Mail, Settings, Send, CheckCircle2, XCircle, Clock, AlertTriangle, History } from 'lucide-react';
import { isAdmin } from '@/lib/auth';
import api from '@/lib/api';

export default function AlertSettings() {
  const [settings, setSettings] = useState({
    alert_email: '',
    delinquency_threshold: 6,
    enabled: true
  });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, historyRes] = await Promise.all([
        api.get('/alerts/settings'),
        api.get('/alerts/history')
      ]);
      setSettings(settingsRes.data);
      setHistory(historyRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/alerts/settings', settings);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      const response = await api.post('/alerts/test-email');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar email de teste');
    } finally {
      setTesting(false);
    }
  };

  const handleCheckAndSend = async () => {
    setChecking(true);
    try {
      const response = await api.post('/alerts/check-and-send');
      if (response.data.alert_sent) {
        toast.success(`Alerta enviado! Taxa atual: ${response.data.delinquency_rate.toFixed(1)}%`);
      } else {
        toast.info(response.data.message);
      }
      loadData(); // Reload history
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao verificar alertas');
    } finally {
      setChecking(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="alert-settings-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight flex items-center gap-3">
          <Bell className="w-9 h-9 text-primary" />
          Configurações de Alertas
        </h1>
        <p className="text-muted-foreground mt-1">Configure alertas automáticos de inadimplência</p>
      </div>

      {!isAdmin() && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Apenas administradores podem alterar as configurações de alertas.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configurações */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurações
            </CardTitle>
            <CardDescription>
              Defina o limite de inadimplência e email para alertas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label>Alertas Habilitados</Label>
                <p className="text-sm text-muted-foreground">
                  Ativar/desativar envio de alertas
                </p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                disabled={!isAdmin()}
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="alert_email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email para Alertas
              </Label>
              <Input
                id="alert_email"
                type="email"
                value={settings.alert_email}
                onChange={(e) => setSettings({ ...settings, alert_email: e.target.value })}
                placeholder="cobranca@empresa.com"
                disabled={!isAdmin()}
                data-testid="alert-email-input"
              />
              <p className="text-xs text-muted-foreground">
                Os alertas serão enviados para este email
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="threshold" className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Limite de Inadimplência (%)
              </Label>
              <Input
                id="threshold"
                type="number"
                min="1"
                max="100"
                step="0.5"
                value={settings.delinquency_threshold}
                onChange={(e) => setSettings({ ...settings, delinquency_threshold: parseFloat(e.target.value) })}
                disabled={!isAdmin()}
                data-testid="threshold-input"
              />
              <p className="text-xs text-muted-foreground">
                Alerta será enviado quando a taxa ultrapassar este valor
              </p>
            </div>

            <Separator />

            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                disabled={saving || !isAdmin()}
                data-testid="save-settings"
              >
                {saving ? 'Salvando...' : 'Salvar Configurações'}
              </Button>
              <Button
                variant="outline"
                onClick={handleTestEmail}
                disabled={testing || !isAdmin()}
                data-testid="test-email"
              >
                {testing ? 'Enviando...' : 'Testar Email'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Verificação Manual */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Verificação Manual
            </CardTitle>
            <CardDescription>
              Verifique a taxa atual e envie alerta se necessário
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Limite configurado:</p>
              <p className="text-3xl font-bold text-primary">{settings.delinquency_threshold}%</p>
            </div>

            <p className="text-sm text-muted-foreground">
              Clique no botão abaixo para verificar a taxa atual de inadimplência.
              Se estiver acima do limite, um alerta será enviado automaticamente.
            </p>

            <Button
              onClick={handleCheckAndSend}
              disabled={checking}
              className="w-full"
              size="lg"
              data-testid="check-and-send"
            >
              {checking ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Verificando...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Verificar e Enviar Alerta
                </>
              )}
            </Button>

            <Alert className="border-blue-200 bg-blue-50">
              <AlertTriangle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                O alerta só será enviado se a taxa de inadimplência estiver acima de {settings.delinquency_threshold}%.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>

      {/* Histórico de Alertas */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Histórico de Alertas
          </CardTitle>
          <CardDescription>
            Últimos alertas enviados pelo sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <Alert>
              <Clock className="h-4 w-4" />
              <AlertDescription>
                Nenhum alerta enviado ainda.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Taxa</TableHead>
                    <TableHead>Limite</TableHead>
                    <TableHead>Enviado Para</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((log, index) => (
                    <TableRow key={log.id || index}>
                      <TableCell className="font-mono text-sm">
                        {formatDateTime(log.sent_at)}
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-red-600">
                          {log.delinquency_rate?.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell>
                        {log.threshold}%
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {log.sent_to}
                      </TableCell>
                      <TableCell className="text-center">
                        {log.success ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                            Enviado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                            <XCircle className="w-3 h-3" />
                            Falhou
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
